import React from "react";
import { Link } from "react-router-dom";
import { Auth } from "aws-amplify";
import {
  IoMdBulb,
  IoIosEye,
  IoIosEyeOff,
  IoIosAlert,
  IoIosArrowRoundForward,
  IoIosArrowBack,
  IoIosCheckmarkCircle,
} from "react-icons/io";
import { mapAuthError } from "./authErrors";
import SocialButtons from "./SocialButtons";
import "./auth.css";

// Step 1: collect name + email + password → Auth.signUp.
// Step 2: enter verification code emailed by Cognito → Auth.confirmSignUp,
//          then auto-Auth.signIn with stored password to drop into the app.

const STEP_FORM = "form";
const STEP_CONFIRM = "confirm";

function readEmailFromQuery(search) {
  const params = new URLSearchParams(search || "");
  return (params.get("email") || "").toLowerCase();
}

export default function Signup(props) {
  // If we land on /signup/confirm directly (eg. from Login when user is
  // not yet confirmed), start at the confirm step with prefilled email.
  const isConfirmRoute =
    props.location && props.location.pathname.endsWith("/confirm");
  const [step, setStep] = React.useState(
    isConfirmRoute ? STEP_CONFIRM : STEP_FORM
  );

  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState(
    isConfirmRoute ? readEmailFromQuery(props.location.search) : ""
  );
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submitForm = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    const cleanName = name.trim();
    if (!cleanName || !cleanEmail || !password) {
      setError("Semua kolom wajib diisi.");
      return;
    }
    if (password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    setLoading(true);
    try {
      await Auth.signUp({
        username: cleanEmail,
        password,
        attributes: { email: cleanEmail, name: cleanName },
      });
      setStep(STEP_CONFIRM);
      setNotice(`Kode verifikasi dikirim ke ${cleanEmail}.`);
      setError("");
    } catch (err) {
      if (err && err.code === "UsernameExistsException") {
        // Account exists but maybe not confirmed — try resending code
        try {
          await Auth.resendSignUp(cleanEmail);
          setStep(STEP_CONFIRM);
          setNotice(
            `Akun sudah pernah dibuat. Kode verifikasi baru dikirim ke ${cleanEmail}.`
          );
        } catch (_) {
          setError(mapAuthError(err));
        }
      } else {
        setError(mapAuthError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitConfirm = async (e) => {
    e.preventDefault();
    setError("");
    const cleanEmail = email.trim().toLowerCase();
    const cleanCode = code.trim();
    if (!cleanEmail || !cleanCode) {
      setError("Email dan kode verifikasi wajib diisi.");
      return;
    }
    setLoading(true);
    try {
      await Auth.confirmSignUp(cleanEmail, cleanCode);
      // Auto-sign-in only works if we have the password from step 1.
      // If user landed on /signup/confirm directly (no password in state),
      // route them to /login with a success message.
      if (password) {
        try {
          await Auth.signIn(cleanEmail, password);
          props.history.push("/");
          return;
        } catch (_) {
          props.history.push("/login");
          return;
        }
      }
      props.history.push("/login");
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    setNotice("");
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      setError("Masukkan email terlebih dahulu.");
      return;
    }
    setLoading(true);
    try {
      await Auth.resendSignUp(cleanEmail);
      setNotice(`Kode baru dikirim ke ${cleanEmail}.`);
    } catch (err) {
      setError(mapAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  if (step === STEP_CONFIRM) {
    return (
      <div className="auth">
        <Link to="/landing" className="auth__brand">
          <IoMdBulb size="1.4em" color="var(--brand-700)" />
          cashflow
        </Link>
        <div className="auth__card">
          <h1 className="auth__title">Verifikasi email</h1>
          <p className="auth__subtitle">
            Kami sudah mengirim kode 6 digit ke email Anda.
          </p>

          {notice && (
            <div className="auth__notice">
              <IoIosCheckmarkCircle /> {notice}
            </div>
          )}
          {error && (
            <div className="auth__alert" role="alert">
              <IoIosAlert size="1.1em" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submitConfirm} noValidate>
            <div className="auth__field">
              <label className="auth__field-label" htmlFor="conf-email">
                Email
              </label>
              <input
                id="conf-email"
                type="email"
                className="auth__input"
                autoComplete="email"
                value={email}
                disabled={loading}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="auth__field">
              <label className="auth__field-label" htmlFor="conf-code">
                Kode verifikasi
              </label>
              <input
                id="conf-code"
                type="text"
                className="auth__input"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={code}
                disabled={loading}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6 digit kode"
                maxLength={10}
              />
            </div>

            <div className="auth__hint-row">
              <button
                type="button"
                className="auth__hint-link"
                onClick={resendCode}
                disabled={loading}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                  fontFamily: "inherit",
                  fontSize: "inherit",
                }}
              >
                Kirim ulang kode
              </button>
            </div>

            <button type="submit" className="auth__submit" disabled={loading}>
              {loading ? (
                <span className="auth__spinner" />
              ) : (
                <>
                  Verifikasi
                  <IoIosArrowRoundForward size="1.3em" />
                </>
              )}
            </button>
          </form>

          <div className="auth__footer">
            Sudah punya akun?
            <Link to="/login">Masuk</Link>
          </div>
        </div>

        <Link to="/landing" className="auth__back">
          <IoIosArrowBack /> Kembali ke beranda
        </Link>
      </div>
    );
  }

  return (
    <div className="auth">
      <Link to="/landing" className="auth__brand">
        <IoMdBulb size="1.4em" color="var(--brand-700)" />
        cashflow
      </Link>
      <div className="auth__card">
        <h1 className="auth__title">Daftar</h1>
        <p className="auth__subtitle">Mulai catat keuanganmu — gratis.</p>

        {error && (
          <div className="auth__alert" role="alert">
            <IoIosAlert size="1.1em" />
            <span>{error}</span>
          </div>
        )}

        <SocialButtons mode="signup" />
        <div className="auth__divider">
          <span>atau dengan email</span>
        </div>

        <form onSubmit={submitForm} noValidate>
          <div className="auth__field">
            <label className="auth__field-label" htmlFor="su-name">
              Nama
            </label>
            <input
              id="su-name"
              type="text"
              className="auth__input"
              autoComplete="name"
              autoFocus
              value={name}
              disabled={loading}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nama lengkap Anda"
            />
          </div>

          <div className="auth__field">
            <label className="auth__field-label" htmlFor="su-email">
              Email
            </label>
            <input
              id="su-email"
              type="email"
              className="auth__input"
              autoComplete="email"
              value={email}
              disabled={loading}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kamu@email.com"
            />
          </div>

          <div className="auth__field">
            <label className="auth__field-label" htmlFor="su-password">
              Password
            </label>
            <div className="auth__password-wrap">
              <input
                id="su-password"
                type={showPw ? "text" : "password"}
                className="auth__input"
                autoComplete="new-password"
                value={password}
                disabled={loading}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimal 8 karakter"
              />
              <button
                type="button"
                className="auth__password-toggle"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? "Sembunyikan password" : "Lihat password"}
                tabIndex={-1}
              >
                {showPw ? <IoIosEyeOff /> : <IoIosEye />}
              </button>
            </div>
          </div>

          <button type="submit" className="auth__submit" disabled={loading}>
            {loading ? (
              <span className="auth__spinner" />
            ) : (
              <>
                Buat akun
                <IoIosArrowRoundForward size="1.3em" />
              </>
            )}
          </button>
        </form>

        <div className="auth__footer">
          Sudah punya akun?
          <Link to="/login">Masuk</Link>
        </div>
      </div>

      <Link to="/landing" className="auth__back">
        <IoIosArrowBack /> Kembali ke beranda
      </Link>
    </div>
  );
}
